import { useEffect, useRef } from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';

import { trackView } from '@/utils';

import type { RouteObject } from 'react-router-dom';

const TRACKING_PARAM_BLACKLIST = new Set(['email', 'token']);
const UNAUTHENTICATED_PATHS = new Set([
  '/auth/discovery-callback',
  '/authenticate',
  '/coach/authenticate',
  '/coach/signin',
  '/mfa',
  '/reset',
  '/signin',
  '/verify-email',
]);

/**
 * MEN-3990: the route tree is passed in rather than imported.
 *
 * `@/appRoutes` builds page elements at module scope, so importing it here put
 * every page that reaches `@/hooks` into a cycle:
 *
 *   <page> -> @/hooks -> useMixpanelRouteTracking -> @/appRoutes -> <page>
 *
 * The app masked it by always entering through `@/Router`, but anything that
 * imported a page first — a story, a test harness — died with "Cannot access
 * '<page>' before initialization". Tracking only needs the route *paths*, so
 * it has no business importing the component tree.
 */
export const buildTrackedPathFromPathname = (
  pathname: string,
  routes: RouteObject[],
): string => {
  const matches = matchRoutes(routes, { pathname }) ?? [];
  if (!matches.length) {
    return '';
  }

  if (matches.some((match) => match.route.path === '*')) {
    return '';
  }

  return matches
    .map((match) => {
      const params = { ...match.params };
      TRACKING_PARAM_BLACKLIST.forEach((key) => {
        delete params[key];
      });

      return Object.keys(params).reduce(
        (currentPath, key) => currentPath.replace(`:${key}`, `[${key}]`),
        match.route.path ?? '',
      );
    })
    .reduce((trackedPath, routePath) => {
      if (!routePath) {
        return trackedPath;
      }

      if (routePath.startsWith('/')) {
        return routePath;
      }

      if (!trackedPath) {
        return routePath;
      }

      return `${trackedPath}/${routePath}`;
    }, '');
};

export const useMixpanelRouteTracking = (routes: RouteObject[]): void => {
  const { pathname, search } = useLocation();
  const lastTrackedPathname = useRef<string | null>(null);

  useEffect(() => {
    if (UNAUTHENTICATED_PATHS.has(pathname)) {
      return;
    }
    if (!pathname || lastTrackedPathname.current === pathname) {
      return;
    }

    lastTrackedPathname.current = pathname;

    const trackedPath = buildTrackedPathFromPathname(pathname, routes);
    if (!trackedPath) {
      return;
    }

    // MEN-5426: send the query present when the page view fires, e.g. email
    // UTMs. A query-only change is not a new page view.
    trackView(trackedPath, Object.fromEntries(new URLSearchParams(search)));
  }, [pathname, routes]);
};
